/**
 * Error text of the dispatch screens (batch 8 T12c1, #3033): the server's `code` (and nothing of its prose) or the HTTP status
 * is turned into a translated sentence the user can act on. Server messages are English developer text and are never shown.
 */

import type { TFunction } from 'i18next';
import { DispatchAccessError, OwnerChangedError } from './dispatchSession';
import { bodyOf, statusOf } from './dispatchOutcome';

/** Codes of the server that have a sentence under `errors.code.*`. */
export const KNOWN_ERROR_CODES: readonly string[] = [
  'purchase_order_not_found',
  'dispatch_not_found',
  'invalid_input',
  'stale_review',
  'operation_key_conflict',
  'dispatch_already_open',
  'dispatch_already_sent',
  'invalid_state',
  'document_frozen',
  'purchase_order_not_approved',
  'supplier_inactive',
  'manual_supplier',
  'dispatch_mode_mismatch',
  'invalid_recipient',
  'unsupported_language',
  'api_not_configured',
  'no_lines',
  'invalid_line',
  'invalid_snapshot',
  'dispatch_not_cancellable',
  'invalid_cursor',
  'order_email_required',
  'settings_conflict',
  'supplier_not_found',
];
const KNOWN_CODES = new Set<string>(KNOWN_ERROR_CODES);

/** Local failures that never reached the server. */
function localName(error: unknown): 'journal_full' | 'invalid_preview' | null {
  const name = (error as { name?: unknown } | null)?.name;
  if (name === 'JournalFullError') return 'journal_full';
  if (name === 'InvalidPreviewError') return 'invalid_preview';
  return null;
}

/** A translated sentence for a failed read or write. `t` is bound to the `procurementDispatch` namespace. */
export function describeDispatchError(t: TFunction, error: unknown): string {
  if (error instanceof DispatchAccessError) return t('errors.noPermission');
  if (error instanceof OwnerChangedError) return t('errors.sessionChanged');
  const local = localName(error);
  if (local === 'journal_full') return t('errors.journalFull');
  if (local === 'invalid_preview') return t('errors.invalidPreview');

  const status = statusOf(error);
  const { code } = bodyOf(error);
  if (code !== undefined && KNOWN_CODES.has(code)) return t(`errors.code.${code}`);
  if (status === null) return t('errors.network');
  if (status === 401) return t('errors.unauthorized');
  if (status === 403) return t('errors.forbidden');
  if (status === 404) return t('errors.notFound');
  if (status === 423) return t('errors.locked');
  if (status === 409) return t('errors.conflict');
  if (status >= 500) return t('errors.server');
  return t('errors.generic');
}

/** The reason the API channel is unavailable for a supplier (`details.reason` of `api_not_configured`), if the server named one. */
export function apiUnavailableReason(error: unknown): string | null {
  const reason = bodyOf(error).details?.['reason'];
  return typeof reason === 'string' ? reason : null;
}
